import { useState, useEffect } from 'react';

import classes from './BookedStudio.module.css';
import { useNavigate } from 'react-router-dom';
import { useSelector } from "react-redux";
import ChatLink from '../ChatLink';
import { useFetchLatestMessage } from '../../hooks/messages/useFetchLatestMessage';
import { useFetchCreativePackageImage } from '../../hooks/creative';
import { useChatRooms } from '../../hooks/messages/useChatRooms';
import { useTextTruncate } from '../../hooks/useTextTruncate';
import { formatBerlinDate, formatBerlinDateTimeRange, isCompletedBookingStatus } from '../../util/bookingDateTime';
import noUserImage from "../../public/fakeUser/noUser.webp";
import { getStudioImageSrc } from '../../util/studioImageUtils';
import { buildCoarseLocationText } from '../../util/locationPrivacy';

const normalizeId = (value) => (value === undefined || value === null ? '' : String(value));

const idsMatch = (left, right) => {
    const normalizedLeft = normalizeId(left);
    const normalizedRight = normalizeId(right);
    return normalizedLeft !== '' && normalizedRight !== '' && normalizedLeft === normalizedRight;
};

const roomMatchesBooking = (room, bookingId) => {
    if (!room || !normalizeId(bookingId)) return false;

    if (idsMatch(room.id, bookingId) || idsMatch(room.linkedBookingID, bookingId)) {
        return true;
    }

    return Array.isArray(room.linkedBookingIDs) && room.linkedBookingIDs.some((linkedId) => idsMatch(linkedId, bookingId));
};

const BookedStudio = ({ studioData, hasNotification }) => {
    const { user } = useSelector((state) => state?.auth);
    const { Studio, booking, creativePackages } = studioData;
    const [formatedText, setFormatedText] = useState("");
    const navigate = useNavigate();

    // Get the first package ID from creativePackages
    const firstPackageId = creativePackages?.[0]?.packageID || booking?.creativePackages?.[0]?.packageID;
    
    // Fetch the first image of the package
    const { firstImage: packageImage, packageName, loading: packageImageLoading } = useFetchCreativePackageImage(firstPackageId);
    const { truncatedText: truncatedStudioName } = useTextTruncate(Studio?.StudioName, {
        maxChars: 70,
        multiline: true
    });
    const { truncatedText: truncatedPackageName } = useTextTruncate(packageName, {
        maxChars: 70,
        multiline: true
    });

    const { latestMessageContent, latestMessageTime } = useFetchLatestMessage(
        Studio?.id,
        user?.id,
        booking?.User?.id,
        {
            chatRoomId: booking?.chatRoomID,
            bookingId: booking?.id
        }
    );
    
    // Get chat rooms to check for unread messages
    const { chatRooms } = useChatRooms(user?.id);
    
    // Find the chat room for this studio and guest user
    const hasBookingChatContext = !!normalizeId(booking?.chatRoomID) || !!normalizeId(booking?.id);
    const chatRoom = chatRooms.find(room => {
        if (booking?.chatRoomID && idsMatch(room.id, booking.chatRoomID)) {
            return true;
        }

        if (booking?.id && roomMatchesBooking(room, booking.id)) {
            return true;
        }

        if (hasBookingChatContext) {
            return false;
        }

        return room.studioID === Studio?.id && room.chatPartner?.id === booking?.User?.id;
    });
    
    // Calculate unread count for this specific chat room
    const unreadCount = chatRoom && user?.id ? (
        chatRoom.hostID === user.id 
            ? (chatRoom.hostUnreadCount || 0)
            : (chatRoom.guestUnreadCount || 0)
    ) : 0;

    // Helper function to format booking details for first message
    const formatBookingDetailsMessage = (booking, studio) => {
        if (!booking || !studio) return "";
        const dateTime = formatBerlinDateTimeRange(booking.startDateTime, booking.endDateTime, {
            dateOptions: {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
            },
            timeSuffix: '',
        });
        const startDate = dateTime?.startDate || '';
        const startTime = dateTime?.startTime || '';
        const endTime = dateTime?.endTime || '';
        
        return `Hallo! Ich schreibe bezüglich der Buchung:\n\n📅 ${startDate}\n⏰ ${startTime} - ${endTime}\n🏢 ${studio.StudioName}\n💰 €${booking.totalPrice}\n\nStatus: ${booking.status === 'CONFIRMED' ? 'Bestätigt' : booking.status === 'PENDING' ? 'Noch offen' : 'Storniert'}`;
    };

    const bookingDateTime = formatBerlinDateTimeRange(booking?.startDateTime, booking?.endDateTime, {
        dateOptions: {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        },
        timeSuffix: '',
    });

    const studio = {
        id: Studio.id,
        image: Studio.processedImages?.[0],
        name: Studio.StudioName,
        location: buildCoarseLocationText(Studio),
        date: bookingDateTime?.startDate || formatBerlinDate(booking?.startDateTime, {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        }),
        startTime: bookingDateTime?.startTime || '',
        endTime: bookingDateTime?.endTime || '',
        price: `${booking.totalPrice} Euro`
    };

    const userBookingTheStudio = {
        id: booking.User.id,
        userImage: booking.User.processedProfilePicture,
        userName: `${booking.User.firstName} ${booking.User.lastName}`,
        message: latestMessageContent || "keine Nachricht",
        messageSentTime: latestMessageTime || new Date(booking.createdAt).getHours() + "h"
    };

    const handleBookingDetails = () => {
        navigate(`/creator/details/${studioData.booking.id}`, {
            state: { userId: booking.User.id, userName: `${booking.User.firstName} ${booking.User.lastName}`, userImage: booking.User.processedProfilePicture }
        });
    };

    useEffect(() => {
        setFormatedText(formatText(userBookingTheStudio?.message));
    }, [userBookingTheStudio?.message]);

    function formatText(text) {
        if (text?.length > 12) {
            return text.substring(0, 12) + '...';
        }
        return text;
    }

    // Helper functions for status display (Creator mode)
    const calculateHoursRemaining = (createdAt) => {
        if (!createdAt) return null;
        const created = new Date(createdAt);
        const now = new Date();
        const hoursPassed = (now - created) / (1000 * 60 * 60);
        const hoursRemaining = Math.max(0, Math.ceil(24 - hoursPassed));
        return hoursRemaining;
    };

    const isBookingExpired = (createdAt) => {
        if (!createdAt) return false;
        const created = new Date(createdAt);
        const now = new Date();
        const hoursPassed = (now - created) / (1000 * 60 * 60);
        return hoursPassed > 24;
    };

    const getStatusDisplay = () => {
        const status = booking?.status;
        const hostName = ''; // Creator is the host
        const guestName = booking?.User?.firstName || '';
        const cancelledBy = booking?.cancelledBy;
        const createdAt = booking?.createdAt;
        const hoursRemaining = calculateHoursRemaining(createdAt);
        const expired = isBookingExpired(createdAt);
        const completed = isCompletedBookingStatus(status);

        // Check if cancelled by guest or host
        const cancelledByGuest = cancelledBy === "GUEST";
        const cancelledByHost = cancelledBy === "HOST";
        const cancelledBySystem = cancelledBy === "SYSTEM";

        if (completed) {
            return {
                message: '',
                statusText: 'Abgeschlossen',
                color: '#A1A7AE'
            };
        }

        if (status === "NO_SHOW") {
            return {
                message: `${guestName} ist nicht erschienen`,
                statusText: 'Nicht erschienen',
                color: '#CE425D'
            };
        }

        if (status === "EXPIRED" || (expired && status === "PENDING")) {
            return {
                message: 'Die Anfrage ist abgelaufen',
                statusText: 'Anfrage abgelaufen',
                color: '#CE425D'
            };
        }

        if (status === "INQUIRY" || (status === "PENDING" && !expired && hoursRemaining === null)) {
            return {
                message: `Erkundigung von ${guestName}`,
                statusText: 'Erkundigung',
                color: '#2788EE'
            };
        }

        if (status === "PENDING" && !expired) {
            return {
                message: `${guestName} hat bei dir angefragt`,
                statusText: `Angefragt: Läuft in ${hoursRemaining} ${hoursRemaining === 1 ? 'Stunde' : 'Stunden'} ab`,
                color: '#F7871E'
            };
        }

        if (status === "WITHDRAWN") {
            return {
                message: `${guestName} hat die Anfrage zurückgezogen`,
                statusText: 'Anfrage zurückgezogen',
                color: '#CE425D'
            };
        }

        if (status === "DECLINED" || status === "REJECTED") {
            return {
                message: `Du hast die Buchungsanfrage abgelehnt`,
                statusText: 'Anfrage abgelehnt',
                color: '#CE425D'
            };
        }

        if (status === "CONFIRMED") {
            return {
                message: `Du hast die Buchung bestätigt`,
                statusText: 'Buchung bestätigt',
                color: '#20BD13'
            };
        }

        if (status === "CANCELLED") {
            // Creator mode
            if (cancelledByGuest) {
                return {
                    message: `${guestName} hat die Buchung storniert`,
                    statusText: 'Vom Entdecker storniert',
                    color: '#CE425D'
                };
            } else if (cancelledByHost) {
                return {
                    message: `Du hast die Buchung storniert`,
                    statusText: 'Vom Creator storniert',
                    color: '#CE425D'
                };
            } else if (cancelledBySystem) {
                return {
                    message: 'Die Buchung wurde automatisch storniert',
                    statusText: 'Storniert',
                    color: '#CE425D'
                };
            } else {
                // Fallback if cancelledBy is not available - assume creator cancelled
                return {
                    message: `Du hast die Buchung storniert`,
                    statusText: 'Storniert',
                    color: '#CE425D'
                };
            }
        }

        // Default fallback
        return {
            message: '',
            statusText: 'Unbekannt',
            color: '#A1A7AE'
        };
    };

    return (
        <div className={`${classes.bookedStudioContainer} ${hasNotification ? classes.hasNotification : ''}`}>
            <div className={classes.overlay} onClick={handleBookingDetails}></div>
            <div className={classes.imageContainer}>
                <img
                  className={classes.studioImage}
                  src={getStudioImageSrc(studio?.image)}
                  alt="Studio"
                />
                {packageImage && (
                    <div className={classes.outsidePackageImageContainer}>
                        <div className={classes.packageImageContainer}>
                            <img src={packageImage} alt="Package" />
                        </div>
                    </div>
                )}
            </div>
            <div style={{width: '172px'}} className={classes.studioInfo}>
                <div>
                    <h3 className={`semiboldLarge ${classes.truncatedTitle}`}>{truncatedStudioName}</h3>
                    <p style={{color: 'var(--dark-grey-3)'}} className="regularXs">{studio?.location}</p>
                </div>
                {packageName && (
                <div className={classes.smallHorizontalLine}></div>
                )}
                <p style={{color: 'var(--dark-grey)'}} className={`semiboldXs ${classes.truncatedPackageName}`}>{truncatedPackageName}</p>
            </div>
            <div className={classes.verticalLine}></div>
            <div style={{width: '110px'}} className={classes.studioInfo}> 
                <h3 className="regularXs">{studio?.date}</h3>
                <p className="regularXs">Start: {studio?.startTime}</p>
                <p className="regularXs">Ende: {studio?.endTime}</p>
            </div>
            <div className={classes.verticalLine}></div>
            <div style={{width: '45px'}} className={classes.studioInfo}>
                <h3 className="regularXs">{studio?.price}</h3>
            </div>
            <div className={classes.verticalLine}></div>
            <div style={{width: '165px'}} className={classes.userContainer}>
                <div className={classes.userInfo}>  
                    <div className={classes.userImage}>
                        <img src={userBookingTheStudio?.userImage || noUserImage} alt="User" />
                    </div>
                    <div className={classes.userText}>
                        <h4 className="regularSmall">{userBookingTheStudio?.userName || "Kein Name"}</h4>
                        <p style={{whiteSpace: 'break-spaces'}} className="regularXs">{formatedText}</p>
                    </div>
                    <div className={classes.smallInfos}>
                        <p className="mediumMini">{userBookingTheStudio?.messageSentTime || "Keine Zeit"}</p>
                        {unreadCount > 0 && (
                            <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#DF315A',
                                flexShrink: 0
                            }}></div>
                        )}
                    </div>
                </div> 
                <ChatLink 
                    userId={userBookingTheStudio?.id}
                    studioId={Studio?.id}
                    studioName={Studio?.StudioName}
                    studioImage={Studio?.processedImages?.[0]}
                    className={`${classes.message} semiboldXs`}
                    creatorNavBar={false}
                    firstMessage={!latestMessageContent ? formatBookingDetailsMessage(booking, Studio) : undefined}
                    chatRoomId={booking?.chatRoomID}
                    bookingId={booking?.id}
                >
                    Nachricht
                </ChatLink>
            </div>
            <div className={classes.verticalLine}></div>
            <div className={classes.statusContainer}>
                {(() => {
                    const statusDisplay = getStatusDisplay();
                    return (
                        <p style={{color: statusDisplay.color}} className="semiboldLarge">
                            {statusDisplay.statusText}
                        </p>
                    );
                })()}
                <button onClick={handleBookingDetails} className="regularSmall">Buchungsdetails</button>
            </div>
            <div className={classes.tabletContainer}>
                <div className={classes.infosContainer}>
                    <p className={`semiboldLarge ${classes.truncatedTitle}`}>{truncatedStudioName}</p>
                    <p className={`semiboldXs ${classes.truncatedPackageName}`}>{truncatedPackageName}</p>
                    <div className={classes.smallHorizontalLine}></div>
                    {(() => {
                        const statusDisplay = getStatusDisplay();
                        return (
                            <p style={{color: statusDisplay.color}} className="semiboldLarge">
                                {statusDisplay.statusText}
                            </p>
                        );
                    })()}
                    <p className="regularXs">{userBookingTheStudio?.userName || "Kein Name"}</p>
                    <p className="regularXs">{studio?.date}, {studio?.startTime} - {studio?.endTime}</p>
                    <p className="regularXs">{studio.price}</p>
                </div>
                <div className={classes.buttonContainer} onClick={handleBookingDetails}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="25" height="19" viewBox="0 0 25 19" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M24.998 9.88046L25.0054 9.88856L24.8538 10.0267L16.0082 18.9919L14.7981 17.7979L22.656 9.83367L14.75 1.15299L16.0069 0.00830078L24.8413 9.70838L25.0067 9.87162L24.998 9.88046Z" fill="#1F1F1F"/>
                        <circle cx="3" cy="10" r="3" fill="#DF315A"/>
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default BookedStudio;
